import { NavLink } from 'react-router-dom';
import { useOwnedTenants } from '../hooks/useOwnedTenants';

/**
 * Admin sidebar navigation. Rendered inside AdminChromeLayout for every
 * authenticated admin page. As new modules land (Auctions, Lots, Reports,
 * Settings) they plug into the `baseSections` array below.
 *
 * The "Set-up" link is prepended ONLY when the owner has a tenant still in
 * `publish_state='draft'` (onboarding). Every existing tenant is 'live', so
 * this stays invisible until a house is actually onboarding — no ghost link.
 */

type NavSection = {
  label: string;
  href: string;
  end?: boolean;
};

const baseSections: readonly NavSection[] = [
  { label: 'Dashboard', href: '/admin/dashboard', end: true },
  { label: 'Brief', href: '/admin/brief' },
  { label: 'Copilot', href: '/admin/copilot' },
  { label: 'Auctions', href: '/admin/auctions' },
  { label: 'Inventory', href: '/admin/inventory' },
  { label: '📸 Catalog', href: '/admin/catalog' },
  { label: 'Taxonomies', href: '/admin/taxonomies' },
  { label: 'Consignment Terms', href: '/admin/consignment-profiles' },
  { label: 'Tax Profiles', href: '/admin/tax-profiles' },
  { label: 'Consignors', href: '/admin/consignors' },
  { label: 'Bidders', href: '/admin/bidders' },
  { label: 'Q&A', href: '/admin/questions' },
  { label: 'Billing & Payouts', href: '/admin/billing' },
  { label: 'Invoices', href: '/admin/invoices' },
  { label: 'Invoice Settings', href: '/admin/invoice-settings' },
  { label: 'House Contact', href: '/admin/house-contact' },
  { label: 'Branding', href: '/admin/branding' },
  { label: 'Sales Intel', href: '/admin/sales-intel' },
  { label: 'Reports', href: '/admin/reports' },
  { label: 'AI Settings', href: '/admin/ai-settings' },
];

export function AdminNav() {
  const owned = useOwnedTenants();
  const showOnboarding = owned.tenants.some((t) => t.publish_state === 'draft');
  const sections: readonly NavSection[] = showOnboarding
    ? [{ label: '✨ Set-up', href: '/admin/onboarding' }, ...baseSections]
    : baseSections;

  return (
    <nav
      aria-label="Admin sections"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
      }}
    >
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.25rem' }}>
        {sections.map((section) => (
          <li key={section.href}>
            <NavLink
              to={section.href}
              {...(section.end ? { end: true } : {})}
              className={({ isActive }) => (isActive ? 'admin-nav-link active' : 'admin-nav-link')}
              style={({ isActive }) => ({
                display: 'block',
                padding: '0.5rem 0.75rem',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 500,
                color: isActive ? 'var(--brand-primary-fg)' : 'var(--text)',
                background: isActive ? 'var(--brand-primary)' : 'transparent',
                textDecoration: 'none',
                transition: 'background 120ms ease, color 120ms ease',
              })}
            >
              {section.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
